import beforeDesks from "./assets/pgl/before-dusty-desks.jpg";
import beforeStations from "./assets/pgl/before-workstations.jpg";
import oldPaste from "./assets/pgl/before-old-thermal-paste.jpg";
import beforeDeskRow from "./assets/pgl/before-desk-row.jpg";
import afterPC from "./assets/pgl/after-gaming-pc.jpg";
import afterLounge from "./assets/pgl/after-lounge-in-use.jpg";
import afterStations from "./assets/pgl/after-gaming-stations.jpg";
import afterReassembly from "./assets/pgl/after-reassembled-computers.jpg";
import cleaningStations from "./assets/pgl/cleaning-workstations.jpg";
import cleaningPanels from "./assets/pgl/cleaning-case-panels.jpg";
import cleaningInterior from "./assets/pgl/cleaning-pc-interior.jpg";
import cleaningDesks from "./assets/pgl/cleaning-desks.jpg";
import cleaningMonitors from "./assets/pgl/cleaning-monitor-area.jpg";
import cleaningGamingDesk from "./assets/pgl/cleaning-gaming-desk.jpg";

const before = [
  { image: beforeDesks, alt: "Dust buildup on the PGL workstation desks before cleaning" },
  { image: beforeStations, alt: "PGL workstation surfaces before cleaning" },
  { image: beforeDeskRow, alt: "PGL workstation row before cleaning" },
  { image: oldPaste, alt: "Old thermal paste on the CPU and removed cooler before replacement" },
];
const after = [
  { image: afterPC, alt: "Reassembled gaming PC with illuminated cooling fans" },
  { image: afterReassembly, alt: "Reassembled computers at PGL after cleaning" },
  { image: afterLounge, alt: "PGL gaming stations in use after servicing" },
  { image: afterStations, alt: "Serviced gaming computers and workstations at PGL" },
];
const cleaning = [
  { image: cleaningStations, alt: "Technician working on PGL gaming workstations" },
  { image: cleaningPanels, alt: "Technicians cleaning removed case panels and dust filters" },
  { image: cleaningInterior, alt: "Technician cleaning the interior of a gaming computer" },
  { image: cleaningDesks, alt: "Technician wiping the workstation desks" },
  { image: cleaningMonitors, alt: "Technician cleaning around a workstation monitor" },
  { image: cleaningGamingDesk, alt: "Detailed cleaning of a gaming desk and peripheral area" },
];

function Photo({ photo, featured = false }) {
  return (
    <a href={photo.image} target="_blank" rel="noreferrer" aria-label={`${photo.alt} (view full photo in a new tab)`} className="block overflow-hidden rounded-xl border border-white/10 bg-slate-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-300">
      <img src={photo.image} alt={photo.alt} loading="lazy" width="1536" height="2048" className={`w-full object-contain ${featured ? "h-80" : "aspect-[3/4] h-auto"}`} />
    </a>
  );
}

export default function PGLProject() {
  return (
    <article className="overflow-hidden">
      <div className="border-b border-white/10 p-6 sm:p-8">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#a8cdef]">Protocol Gaming Lounge (PGL)</p>
        <h4 className="mt-4 text-sm font-semibold text-slate-100">Project overview</h4>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">We carried out detailed cleaning and maintenance of 22 gaming computers at Protocol Gaming Lounge (PGL). The work included removing dust from computer interiors, cooling fans, case panels, and filters, cleaning the workstation areas, and replacing old CPU thermal paste with Noctua NT-H2 before reassembling the computers.</p>
        <div className="mt-5 grid gap-3 sm:grid-cols-3">
          {[{ label: "Computers serviced", value: "22 gaming PCs" }, { label: "Service", value: "Detailed cleaning" }, { label: "Thermal paste", value: "Noctua NT-H2" }].map((item) => (
            <div key={item.label} className="rounded-xl border border-white/10 bg-sky-400/5 p-4">
              <p className="text-xs text-slate-400">{item.label}</p>
              <p className="mt-1 font-semibold text-slate-50">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="space-y-8 p-6 sm:p-8">
        <section aria-labelledby="pgl-comparison-title">
          <h4 id="pgl-comparison-title" className="text-lg font-semibold text-white">Before & after</h4>
          <p className="mt-2 text-sm text-slate-400">From dust buildup and old thermal paste to cleaned workstations and reassembled gaming PCs. Select any photo to view it at full size.</p>
          <div className="mt-5 grid gap-6 sm:grid-cols-2">
            {[{ label: "Before", photos: before, color: "text-amber-300" }, { label: "After", photos: after, color: "text-emerald-300" }].map((stage) => (
              <section key={stage.label} aria-label={`${stage.label} cleaning photos`}>
                <h5 className={`mb-3 text-sm font-semibold uppercase tracking-[0.15em] ${stage.color}`}>{stage.label}</h5>
                <Photo photo={stage.photos[0]} featured />
                <div className="mt-3 grid grid-cols-2 gap-3">
                  {stage.photos.slice(1).map((photo) => <Photo key={photo.image} photo={photo} />)}
                </div>
              </section>
            ))}
          </div>
        </section>
        <section aria-labelledby="pgl-cleaning-title">
          <h4 id="pgl-cleaning-title" className="text-lg font-semibold text-white">During the cleaning</h4>
          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {cleaning.map((photo) => <Photo key={photo.image} photo={photo} />)}
          </div>
        </section>
        <section aria-labelledby="pgl-videos-title">
          <h4 id="pgl-videos-title" className="text-lg font-semibold text-white">Project videos</h4>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {[1, 2].map((number) => {
              const videoUrl = `${process.env.PUBLIC_URL}/media/pgl/cleaning-0${number}.mp4`;
              return (
                <div key={number} className="overflow-hidden rounded-xl border border-white/10 bg-slate-950">
                  <video controls playsInline preload="none" aria-label={`PGL cleaning video ${number}`} className="h-72 w-full object-contain">
                    <source src={videoUrl} type="video/mp4" />
                    Your browser does not support embedded video. <a href={videoUrl}>Open the video</a>.
                  </video>
                  <a href={videoUrl} target="_blank" rel="noreferrer" className="block p-3 text-xs font-semibold text-sky-300 hover:underline">Open video {number}<span className="sr-only"> in a new tab</span></a>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </article>
  );
}
